export class LayerPreviewCoordinator<T> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private rerunRequested = false;
  private disposed = false;

  public constructor(
    private readonly exportPreviews: () => Promise<T>,
    private readonly applyPreviews: (previews: T) => void,
    private readonly debounceMs = 100,
  ) {}

  public request(): void {
    if (this.disposed) return;
    if (this.running) {
      this.rerunRequested = true;
      return;
    }
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, this.debounceMs);
  }

  public requestImmediately(): void {
    if (this.disposed) return;
    if (this.running) {
      this.rerunRequested = true;
      return;
    }
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    void this.run();
  }

  public dispose(): void {
    this.disposed = true;
    this.rerunRequested = false;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private async run(): Promise<void> {
    if (this.running || this.disposed) return;
    this.running = true;
    try {
      const previews = await this.exportPreviews();
      if (!this.disposed) this.applyPreviews(previews);
    } catch {
      // Layer previews are decorative. A failed export must not interrupt art.
    } finally {
      this.running = false;
      if (this.rerunRequested && !this.disposed) {
        this.rerunRequested = false;
        this.request();
      }
    }
  }
}
